import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { PauseCircle, PlayCircle } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { logoSrc, orgQuery, useRemoveLogo, useUpdateOrg, useUploadLogo } from '@/api/queries'
import type { Organization } from '@/api/types'
import { useCompanyCaller } from '@/app/company'
import { LogoPicker, type PickedLogo } from '@/app/logo-picker'
import { NothingForRole } from '@/app/nothing-for-role'
import { OnlinePaymentSection } from '@/app/online-payment'
import { SettingsTabs } from '@/app/settings-tabs'
import { canEditSettings, canRenameCompany, canSuspendCompany } from '@/domain/abilities'
import { errorText } from '@/domain/errors'
import { canManageOnlinePayment, canSeeOnlinePayment } from '@/domain/online-sale'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { Button } from '@/ui/button'
import { Field } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { ConfirmDialog, SidePanel } from '@/ui/overlays'
import { Page, PageHeader } from '@/ui/page'
import { SettingsRow, SettingsSection } from '@/ui/settings'
import { LoadingBlock } from '@/ui/spinner'

export const Route = createFileRoute('/_app/c/$orgId/settings/')({
  component: CompanySettings,
})

type News =
  | Readonly<{ kind: 'uploaded' | 'removed' | 'suspended' | 'restored' }>
  | Readonly<{ kind: 'renamed'; name: string }>

const newsText = (news: News): string =>
  news.kind === 'renamed'
    ? t.settings.renamed(news.name)
    : news.kind === 'uploaded'
      ? t.settings.uploaded
      : news.kind === 'removed'
        ? t.settings.removed
        : news.kind === 'suspended'
          ? t.settings.suspended
          : t.settings.restored

/// Налаштування компанії: the name, the logo, online payment and — for gods —
/// whether the company is open at all. The page is for gods, `settings ≥
/// edit` (the logo's rule) and `finance = manage` (the merchant's rule);
/// renaming needs `settings = manage`, suspending a god. «Онлайн-оплата» is
/// shown from `finance ≥ view` and changed from `finance = manage`. The
/// server applies the same rules; anyone else is not even shown the link.
function CompanySettings() {
  const { orgId } = Route.useParams()
  const { caller } = useCompanyCaller(orgId)
  const company = canEditSettings(caller)
  const payment = canSeeOnlinePayment(caller)
  const allowed = company || canManageOnlinePayment(caller)
  const org = useQuery({ ...orgQuery(orgId), enabled: company })
  const upload = useUploadLogo()
  const remove = useRemoveLogo()
  const [picked, setPicked] = useState<PickedLogo | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [news, setNews] = useState<News | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [switching, setSwitching] = useState(false)
  const update = useUpdateOrg(orgId)
  const rename = canRenameCompany(caller)
  const suspend = canSuspendCompany(caller)

  if (!allowed) return <NothingForRole />

  const forget = () => {
    if (picked) URL.revokeObjectURL(picked.previewUrl)
    setPicked(null)
  }

  // A new file goes up at once: picking it IS the decision, and a second
  // «Зберегти» under one picture is a step nobody needs.
  const onPick = (next: PickedLogo) => {
    setPicked(next)
    setNews(null)
    remove.reset()
    upload.mutate(
      { orgId, file: next.file },
      {
        onSuccess: () => {
          URL.revokeObjectURL(next.previewUrl)
          setPicked(null)
          setNews({ kind: 'uploaded' })
        },
      },
    )
  }

  const serverError = upload.isError ? errorText(upload.error) : null

  return (
    <Page>
      <PageHeader title={t.settings.title} subtitle={t.settings.subtitle} tabs={<SettingsTabs orgId={orgId} caller={caller} />} />

      {!company ? (
        <div className="flex max-w-[640px] flex-col gap-8">
          {payment ? <OnlinePaymentSection orgId={orgId} manage={canManageOnlinePayment(caller)} /> : null}
        </div>
      ) : org.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : org.isError ? (
        <InlineBanner
          kind="error"
          message={errorText(org.error)}
          action={{ label: t.app.retry, onClick: () => void org.refetch() }}
        />
      ) : (
        <div className="flex max-w-[640px] flex-col gap-8">
          {news ? <InlineBanner kind="done" message={newsText(news)} /> : null}
          {org.data.status === 'suspended' ? (
            <InlineBanner kind="stale" message={t.settings.suspendedBanner} />
          ) : null}

          <SettingsSection
            id="company"
            title={t.settings.company}
            footer={rename ? t.settings.nameFootnote : t.settings.nameReadOnly}
          >
            <SettingsRow
              title={t.settings.name}
              value={org.data.name}
              {...(rename
                ? {
                    onClick: () => {
                      setNews(null)
                      update.reset()
                      setRenaming(true)
                    },
                  }
                : {})}
            />
          </SettingsSection>

          <SettingsSection
            id="logo"
            title={t.settings.logo}
            meta={org.data.logoUrl ? undefined : t.settings.noLogo}
            footer={t.settings.logoFootnote}
          >
            <LogoPicker
              bare
              label={t.settings.logo}
              companyName={org.data.name}
              picked={picked}
              currentSrc={logoSrc(org.data.logoUrl)}
              onPick={onPick}
              onRefuse={() => setNews(null)}
              onRemove={() => {
                // A file that failed to go up is just dropped; a logo the
                // company has is removed only after a question.
                if (picked) {
                  forget()
                  upload.reset()
                  return
                }
                remove.reset()
                setConfirming(true)
              }}
              busy={upload.isPending}
              error={serverError}
            />
          </SettingsSection>

          {suspend ? (
            <SettingsSection id="state" title={t.settings.state} footer={t.settings.stateFootnote}>
              <SettingsRow
                title={t.settings.stateNow}
                value={
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      aria-hidden="true"
                      className={cn('size-1.5 rounded-full', org.data.status === 'suspended' ? 'bg-warning' : 'bg-success')}
                    />
                    {t.companies.status[org.data.status] ?? org.data.status}
                  </span>
                }
              />
              <SettingsRow
                icon={
                  org.data.status === 'suspended' ? (
                    <PlayCircle className="size-[18px]" />
                  ) : (
                    <PauseCircle className="size-[18px]" />
                  )
                }
                title={org.data.status === 'suspended' ? t.settings.restore : t.settings.suspend}
                destructive={org.data.status !== 'suspended'}
                onClick={() => {
                  setNews(null)
                  update.reset()
                  setSwitching(true)
                }}
              />
            </SettingsSection>
          ) : null}

          {payment ? <OnlinePaymentSection orgId={orgId} manage={canManageOnlinePayment(caller)} /> : null}
        </div>
      )}

      {renaming && org.data ? (
        <RenamePanel
          org={org.data}
          busy={update.isPending}
          error={update.isError ? errorText(update.error) : null}
          onClose={() => setRenaming(false)}
          onSave={(name) =>
            update.mutate(
              { name },
              {
                onSuccess: (row) => {
                  setRenaming(false)
                  setNews({ kind: 'renamed', name: row.name })
                },
              },
            )
          }
        />
      ) : null}

      {suspend && org.data ? (
        <ConfirmDialog
          open={switching}
          onOpenChange={setSwitching}
          destructive={org.data.status !== 'suspended'}
          title={org.data.status === 'suspended' ? t.settings.restoreTitle : t.settings.suspendTitle}
          message={
            org.data.status === 'suspended'
              ? t.settings.restoreMessage
              : t.settings.suspendMessage(org.data.name)
          }
          confirmLabel={
            org.data.status === 'suspended' ? t.settings.restoreConfirm : t.settings.suspendConfirm
          }
          busy={update.isPending}
          error={update.isError ? <InlineBanner kind="error" message={errorText(update.error)} /> : null}
          onConfirm={() => {
            const next = org.data.status === 'suspended' ? 'active' : 'suspended'
            update.mutate(
              { status: next },
              {
                onSuccess: () => {
                  setSwitching(false)
                  setNews({ kind: next === 'suspended' ? 'suspended' : 'restored' })
                },
              },
            )
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t.settings.removeTitle}
        message={t.settings.removeMessage}
        confirmLabel={t.settings.removeConfirm}
        busy={remove.isPending}
        error={remove.isError ? <InlineBanner kind="error" message={errorText(remove.error)} /> : null}
        onConfirm={() =>
          remove.mutate(orgId, {
            onSuccess: () => {
              setConfirming(false)
              setNews({ kind: 'removed' })
            },
          })
        }
      />
    </Page>
  )
}

function RenamePanel({
  org,
  busy,
  error,
  onClose,
  onSave,
}: {
  org: Organization
  busy: boolean
  error: string | null
  onClose: () => void
  onSave: (name: string) => void
}) {
  const [name, setName] = useState(org.name)
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const unchanged = name.trim() === org.name

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim()) {
      setNameError(t.settings.nameRequired)
      return
    }
    if (unchanged) return onClose()
    onSave(name.trim())
  }

  return (
    <SidePanel
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title={t.settings.renameTitle}
      onSubmit={submit}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t.app.cancel}
          </Button>
          <Button type="submit" variant="primary" busy={busy} disabled={unchanged}>
            {busy ? t.app.saving : t.app.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-7">
        <Field
          autoFocus
          label={t.settings.name}
          value={name}
          maxLength={200}
          error={nameError}
          hint={t.settings.nameFootnote}
          onChange={(e) => {
            setName(e.target.value)
            if (e.target.value.trim()) setNameError(undefined)
          }}
        />
        {error ? <InlineBanner kind="error" message={error} /> : null}
      </div>
    </SidePanel>
  )
}
